import React from 'react';
import { useQuery } from 'react-query';
import { crmFormsApi } from '../../services/api';
import { apiErrorMessage, pct } from '../../utils/forms';

const BAR = '#CD19F9'; // single-series magnitude: one hue (app primary)

const Kpi = ({ label, value, sub }) => (
  <div className="bg-surface-container-low rounded-xl p-4">
    <p className="text-[10px] font-bold uppercase tracking-wider text-secondary">{label}</p>
    <p className="text-2xl font-extrabold font-headline text-on-surface mt-1">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

// Thin horizontal bar, 4px rounded data end, anchored to the baseline. Value text stays in ink.
function BarRow({ label, value, max, right, title }) {
  const w = max > 0 ? Math.max((value / max) * 100, value > 0 ? 1.5 : 0) : 0;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(120px,220px)_minmax(0,1fr)_auto] gap-x-3 gap-y-1 items-center" title={title}>
      <span className="text-sm text-on-surface truncate">{label}</span>
      <div className="col-span-2 sm:col-span-1 order-3 sm:order-none h-3 bg-surface-container rounded-r-[4px] sm:rounded-none">
        <div className="h-3 rounded-r-[4px]" style={{ width: `${w}%`, background: BAR }} />
      </div>
      <span className="text-sm font-bold text-on-surface tabular-nums text-right">{right}</span>
    </div>
  );
}

export default function StatsTab({ formId }) {
  const { data, isLoading, error, refetch, isFetching } = useQuery(['crm-form-stats', formId], () => crmFormsApi.getStats(formId));
  const s = data?.data?.stats;

  if (isLoading) return <p className="text-slate-500">Carregando...</p>;
  if (error) return <p className="text-red-600">{apiErrorMessage(error, 'Erro ao carregar estatísticas')}</p>;
  if (!s) return null;

  // Visits are counted approximately (bots filtered, cookie-less revisits), so completes can exceed them.
  const undercounted = s.completeCount > s.totalVisits;
  const conversion = Math.min(s.conversionRate, 1);
  const abandonRate = s.startedCount > 0 ? s.abandonedCount / s.startedCount : 0;
  const startRate = s.totalVisits > 0 ? s.startedCount / s.totalVisits : 0;
  const funnel = [
    { label: 'Visitas', value: s.totalVisits, right: String(s.totalVisits) },
    { label: 'Iniciados', value: s.startedCount, right: `${s.startedCount} · ${pct(startRate)}` },
    { label: 'Completos', value: s.completeCount, right: `${s.completeCount} · ${pct(conversion)}` },
  ];
  const drop = s.dropoffByQuestion || [];
  const maxDrop = Math.max(0, ...drop.map((d) => d.dropoffCount));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" className="text-sm text-primary font-bold" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      {undercounted && (
        <div className="rounded-xl border border-yellow-300 bg-yellow-50 text-yellow-900 text-sm p-3 flex items-start gap-2" role="status">
          <span className="material-symbols-outlined text-[18px]">info</span>
          <span>Há mais respostas completas do que visitas: as visitas podem estar subcontadas. A conversão é limitada a 100%.</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Visitas" value={s.totalVisits} />
        <Kpi label="Conversão" value={pct(conversion)} sub="completos / visitas" />
        <Kpi label="Completos" value={s.completeCount} />
        <Kpi label="Abandonados" value={s.abandonedCount} sub={`${pct(abandonRate)} dos iniciados`} />
      </div>

      <div className="card p-5 space-y-3">
        <h3 className="font-bold font-headline">Funil</h3>
        {s.totalVisits === 0 && s.startedCount === 0 ? (
          <p className="text-sm text-slate-500">Ainda sem dados.</p>
        ) : funnel.map((f) => (
          <BarRow key={f.label} label={f.label} value={f.value} max={s.totalVisits || s.startedCount} right={f.right} title={`${f.label}: ${f.value}`} />
        ))}
      </div>

      <div className="card p-5 space-y-3">
        <div>
          <h3 className="font-bold font-headline">Desistência por pergunta</h3>
          <p className="text-xs text-slate-500">Última pergunta respondida por quem parou antes de concluir. Percentual sobre os iniciados.</p>
        </div>
        {drop.length === 0 ? <p className="text-sm text-slate-500">Nenhuma pergunta.</p> : drop.map((d, i) => (
          <BarRow
            key={d.questionId} label={`${i + 1}. ${d.label}`} value={d.dropoffCount} max={maxDrop}
            right={`${d.dropoffCount} · ${pct(d.dropoffRate)}`}
            title={`${d.label}: ${d.dropoffCount} desistências (${pct(d.dropoffRate)})`}
          />
        ))}
      </div>
    </div>
  );
}
